// @ts-nocheck
import { apiBaseUrl } from '../api/client';
import { getCurrentUser, onUserChange } from '../auth/session';
import { openAuthModal } from './auth-flow';
import { showToast } from '../components/toast/toast';
import { t } from '../i18n';
import { translateApiMessage } from '../i18n/api-errors';
import { updateGraveCandleCount, updateGraveFlowerCount } from '../map/candle-glow';
import { handleEntitlementError } from './pethernity';
import { syncGardenDocumentTitle } from './garden-location';

let currentHeadstoneId = null;
let currentIsManager = false;
let tributesState = null;

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text ?? '';
  return div.innerHTML;
}

async function apiFetch(path, options = {}) {
  const headers = { ...(options.headers || {}) };
  if (options.body && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${apiBaseUrl}${path}`, {
    credentials: 'include',
    ...options,
    headers,
  });
  const body = res.status === 204 ? null : await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body?.message || 'Request failed');
    err.status = res.status;
    err.code = body?.code;
    throw err;
  }
  return body;
}

function formatTributeDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function hasCommentText(content) {
  return typeof content === 'string' && content.trim().length > 0;
}

function renderGuestMessages(messages, isManager) {
  const list = document.getElementById('view-guest-messages');
  if (!list) return;

  const visible = (messages ?? []).filter((msg) => hasCommentText(msg.content));

  if (!visible.length) {
    list.innerHTML = `<p class="view-tributes-empty">${t('view.noMessages')}</p>`;
    return;
  }

  list.innerHTML = visible
    .map((msg) => {
      const deleteBtn = isManager
        ? `<button type="button" class="view-guest-delete" onclick="deleteGuestMessageFromView('${escapeHtml(msg.id)}')" aria-label="${t('view.deleteMessage')}">×</button>`
        : '';
      return `
        <article class="view-guest-item">
          <div class="view-guest-item-header">
            <span class="view-guest-author">${escapeHtml(msg.author.displayName)}</span>
            <span class="view-guest-date">${escapeHtml(formatTributeDate(msg.createdAt))}</span>
            ${deleteBtn}
          </div>
          <p class="view-guest-content">${escapeHtml(msg.content)}</p>
        </article>
      `;
    })
    .join('');
}

function updateTributesUI() {
  const section = document.getElementById('view-tributes-section');
  const countEl = document.getElementById('view-candle-count');
  const flowerCountEl = document.getElementById('view-flower-count');
  const candleBtn = document.getElementById('view-candle-btn');
  const flowerBtn = document.getElementById('view-flower-btn');
  const guestForm = document.getElementById('view-guest-form');
  const guestLogin = document.getElementById('view-guest-login-cta');
  const guestCooldown = document.getElementById('view-guest-cooldown');
  const candleCooldown = document.getElementById('view-candle-cooldown');
  const flowerCooldown = document.getElementById('view-flower-cooldown');
  const user = getCurrentUser();

  if (!section || !tributesState) return;

  section.hidden = false;
  if (countEl) {
    const n = tributesState.candleCount ?? 0;
    countEl.textContent =
      n === 1 ? t('view.candleOne') : t('view.candleMany', { count: n });
  }
  if (flowerCountEl) {
    const n = tributesState.flowerCount ?? 0;
    flowerCountEl.textContent =
      n === 1 ? t('pethernity.flowerOne') : t('pethernity.flowerMany', { count: n });
  }

  const showLogin = !user;
  const showGuestActions = user && !currentIsManager;

  if (guestLogin) guestLogin.hidden = !showLogin;
  if (guestForm) guestForm.hidden = !showGuestActions;
  if (candleBtn) {
    candleBtn.hidden = showLogin;
    candleBtn.disabled = showLogin || !tributesState.userCanCandle;
  }
  if (flowerBtn) {
    flowerBtn.hidden = showLogin || currentIsManager;
    flowerBtn.disabled = showLogin || currentIsManager || !tributesState.userCanFlower;
  }

  if (guestCooldown) {
    guestCooldown.hidden = !(showGuestActions && !tributesState.userCanMessage);
  }
  if (candleCooldown) {
    candleCooldown.hidden = showLogin || tributesState.userCanCandle;
  }
  if (flowerCooldown) {
    flowerCooldown.hidden = !(showGuestActions && !tributesState.userCanFlower);
  }

  if (showGuestActions && guestForm) {
    const textarea = document.getElementById('view-guest-input');
    const submitBtn = document.getElementById('view-guest-submit');
    const canSend = tributesState.userCanMessage;
    if (textarea) textarea.disabled = !canSend;
    if (submitBtn) submitBtn.disabled = !canSend;
  }

  renderGuestMessages(tributesState.guestMessages, currentIsManager);
}

export async function loadTributesForView(headstoneId, isManager) {
  currentHeadstoneId = headstoneId;
  currentIsManager = !!isManager;
  const section = document.getElementById('view-tributes-section');
  if (section) section.hidden = true;

  try {
    tributesState = await apiFetch(`/headstones/${headstoneId}/tributes`);
    updateTributesUI();
  } catch (err) {
    console.warn('Failed to load tributes:', err);
    tributesState = {
      candleCount: 0,
      flowerCount: 0,
      guestMessages: [],
      candles: [],
      flowers: [],
      userCanCandle: false,
      userCanMessage: false,
      userCanFlower: false,
      userCanReplyCandles: false,
    };
    updateTributesUI();
  }
}

export function resetTributesView() {
  currentHeadstoneId = null;
  currentIsManager = false;
  tributesState = null;
  const section = document.getElementById('view-tributes-section');
  if (section) section.hidden = true;
}

function triggerCandleAnimation() {
  const icon = document.getElementById('view-candle-icon');
  if (!icon) return;
  icon.classList.remove('view-candle-lit');
  void icon.offsetWidth;
  icon.classList.add('view-candle-lit');
}

export async function lightCandleFromView() {
  if (!getCurrentUser()) {
    openAuthModal();
    return;
  }
  if (!currentHeadstoneId) return;

  const btn = document.getElementById('view-candle-btn');
  if (btn) btn.disabled = true;

  try {
    const result = await apiFetch(`/headstones/${currentHeadstoneId}/candles`, {
      method: 'POST',
    });
    tributesState = {
      ...tributesState,
      candleCount: result.candleCount,
      userCanCandle: false,
    };
    updateTributesUI();
    updateGraveCandleCount(currentHeadstoneId, result.candleCount);
    triggerCandleAnimation();
    showToast(t('view.candleLit'));
  } catch (err) {
    if (!handleEntitlementError(err)) {
      showToast(translateApiMessage(err.message) || t('view.candleFailed'));
    }
    updateTributesUI();
  }
}

export async function giftFlowerFromView() {
  if (!getCurrentUser()) {
    openAuthModal();
    return;
  }
  if (!currentHeadstoneId || currentIsManager) return;

  const btn = document.getElementById('view-flower-btn');
  if (btn) btn.disabled = true;

  try {
    const result = await apiFetch(`/headstones/${currentHeadstoneId}/flowers`, {
      method: 'POST',
    });
    tributesState = {
      ...tributesState,
      flowerCount: result.flowerCount,
      userCanFlower: false,
    };
    updateTributesUI();
    updateGraveFlowerCount(currentHeadstoneId, result.flowerCount);
    showToast(t('pethernity.flowerGiven'));
  } catch (err) {
    if (handleEntitlementError(err, t('pethernity.required'))) {
      updateTributesUI();
      return;
    }
    showToast(translateApiMessage(err.message) || t('pethernity.flowerFailed'));
    updateTributesUI();
  }
}

export async function submitGuestMessageFromView(event) {
  event?.preventDefault();
  if (!getCurrentUser()) {
    openAuthModal();
    return;
  }
  if (!currentHeadstoneId || currentIsManager) return;

  const textarea = document.getElementById('view-guest-input');
  const submitBtn = document.getElementById('view-guest-submit');
  const content = textarea?.value?.trim();
  if (!content) return;

  if (submitBtn) submitBtn.disabled = true;

  try {
    const message = await apiFetch(`/headstones/${currentHeadstoneId}/guest-messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
    tributesState = {
      ...tributesState,
      guestMessages: [message, ...(tributesState?.guestMessages || [])].slice(0, 20),
      userCanMessage: false,
    };
    if (textarea) textarea.value = '';
    updateTributesUI();
    showToast(t('view.messageSent'));
  } catch (err) {
    showToast(translateApiMessage(err.message) || t('view.messageFailed'));
  } finally {
    if (submitBtn) submitBtn.disabled = !tributesState?.userCanMessage;
  }
}

export async function deleteGuestMessageFromView(messageId) {
  if (!currentHeadstoneId || !currentIsManager || !messageId) return;

  try {
    await apiFetch(
      `/headstones/${currentHeadstoneId}/guest-messages/${messageId}`,
      { method: 'DELETE' },
    );
    tributesState = {
      ...tributesState,
      guestMessages: (tributesState?.guestMessages || []).filter((m) => m.id !== messageId),
    };
    updateTributesUI();
    showToast(t('view.messageDeleted'));
  } catch (err) {
    showToast(translateApiMessage(err.message) || t('view.deleteFailed'));
  }
}

export function openTributesLogin() {
  openAuthModal('login', t('auth.loginForTribute'));
}

export function refreshViewModalOnLocale() {
  if (tributesState) updateTributesUI();
  const viewed = (window as any).__viewingHeadstone;
  syncGardenDocumentTitle(viewed?.name ?? null);
}

export function initMemorialTributes() {
  const form = document.getElementById('view-guest-form');
  if (form && !form.dataset.tributesBound) {
    form.dataset.tributesBound = '1';
    form.addEventListener('submit', (e) => {
      submitGuestMessageFromView(e).catch(() => null);
    });
  }

  onUserChange(() => {
    if (!currentHeadstoneId) return;
    loadTributesForView(currentHeadstoneId, currentIsManager).catch(() => null);
  });
}
